import React, { useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { ExternalLink } from 'lucide-react';
import { useQuery, useQueryCache } from 'react-query';
import { Icon } from 'src/common/components/Icon';
import Modal, { ModalRefProps } from 'src/common/components/Modal';
import { OrbitIcons } from 'src/common/constants.';

import {
  fetchCallReports,
  fetchSatisfactionMetrics,
  updateCallReport,
  bulkResolveReports,
} from '../api';
import type { CallReport, ReportStatus, ReportType, ReportValue } from '../api';

import RecordingPlayer from './RecordingPlayer';

const REPORT_TYPE_LABELS: Record<string, string> = {
  call_quality: 'Calidad de llamada',
  interference: 'Interferencia',
  dropped_call: 'Llamada caída',
  echo: 'Eco',
  one_way_audio: 'Audio unidireccional',
  other: 'Otro',
};

const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  open: 'Abierto',
  in_progress: 'En progreso',
  resolved: 'Resuelto',
  dismissed: 'Descartado',
};

const REPORT_STATUS_COLORS: Record<ReportStatus, { bg: string; text: string }> = {
  open: { bg: 'bg-blue-100', text: 'text-blue-700' },
  in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  resolved: { bg: 'bg-green-100', text: 'text-green-700' },
  dismissed: { bg: 'bg-gray-100', text: 'text-gray-500' },
};

const ALL_STATUSES: ReportStatus[] = ['open', 'in_progress', 'resolved', 'dismissed'];

const ALL_REPORT_TYPES: ReportType[] = [
  'call_quality',
  'interference',
  'dropped_call',
  'echo',
  'one_way_audio',
  'other',
];

type SatisfactionGroupBy = 'day' | 'week' | 'month';

const GROUP_BY_LABELS: Record<SatisfactionGroupBy, string> = {
  day: 'Diario',
  week: 'Semanal',
  month: 'Mensual',
};

const TIMELINE_TITLE: Record<SatisfactionGroupBy, string> = {
  day: 'Tendencia diaria',
  week: 'Tendencia semanal',
  month: 'Tendencia mensual',
};

function formatReportDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-PR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const openStandaloneWindow = (accountId: number): void => {
  const standaloneUrl = `${window.location.origin}/app/phone-settings/reports/${accountId}`;
  window.open(
    standaloneUrl,
    'ReportsDashboard',
    'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no'
  );
};

interface SectionProps {
  title: string;
  icon: string;
  children: React.ReactNode;
  right?: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, icon, children, right }) => (
  <section className="mb-6">
    <div className="flex items-center justify-between gap-2 mb-3">
      <h3 className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-wide text-gray-500">
        <span className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded">
          <Icon name={icon} size={16} className="text-blue-600" />
        </span>
        {title}
      </h3>
      {right}
    </div>
    <div className="rounded-xl overflow-hidden bg-white border border-gray-200 shadow-sm">
      {children}
    </div>
  </section>
);

const DeltaBadge: React.FC<{
  current: number;
  previous: number;
  suffix?: string;
  invert?: boolean;
}> = ({ current, previous, suffix = '', invert = false }) => {
  if (previous === 0) return null;
  const diff = current - previous;
  if (diff === 0) return null;
  const pct = Math.round((diff / previous) * 100);
  const isPositive = invert ? diff < 0 : diff > 0;
  return (
    <span
      className={classNames(
        'mt-0.5 inline-block rounded-full px-1.5 py-0.5 text-[9px] font-bold',
        isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
      )}
    >
      {diff > 0 ? '+' : ''}
      {pct}%{suffix}
    </span>
  );
};

export const ReportsDashboard: React.FC<{
  accountId: number;
  isStandalone?: boolean;
}> = ({ accountId, isStandalone = false }) => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ReportStatus | ''>('');
  const [valueFilter, setValueFilter] = useState<ReportValue | ''>('negative');
  const [reportTypeFilter, setReportTypeFilter] = useState<ReportType | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [satisfactionGroupBy, setSatisfactionGroupBy] = useState<SatisfactionGroupBy>('month');
  const [expandedReportId, setExpandedReportId] = useState<number | null>(null);
  const [resolvedNoteText, setResolvedNoteText] = useState('');
  const [updatingReportId, setUpdatingReportId] = useState<number | null>(null);
  const [bulkResolving, setBulkResolving] = useState(false);
  const bulkResolveModalRef = useRef<ModalRefProps>(null);
  const queryCache = useQueryCache();

  const {
    data: reportsPayload,
    isLoading: reportsLoading,
    isError: reportsError,
  } = useQuery(
    [
      'reports-dashboard',
      accountId,
      page,
      statusFilter,
      valueFilter,
      reportTypeFilter,
      dateFrom,
      dateTo,
    ],
    () =>
      fetchCallReports(accountId, {
        page,
        pageSize: 20,
        ...(statusFilter ? { reportStatus: statusFilter } : {}),
        ...(valueFilter ? { reportValue: valueFilter } : {}),
        ...(reportTypeFilter ? { reportType: reportTypeFilter } : {}),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
      }),
    { refetchOnWindowFocus: false, retry: 1 }
  );

  const { data: satisfactionData, isLoading: satisfactionLoading } = useQuery(
    ['reports-satisfaction', accountId, satisfactionGroupBy, reportTypeFilter, dateFrom, dateTo],
    () =>
      fetchSatisfactionMetrics(accountId, {
        groupBy: satisfactionGroupBy,
        ...(reportTypeFilter ? { reportType: reportTypeFilter } : {}),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
      }),
    { refetchOnWindowFocus: false, retry: 1 }
  );

  const previousPeriodRange = useMemo(() => {
    if (!dateFrom || !dateTo) return null;
    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    const diffMs = to.getTime() - from.getTime();
    if (diffMs <= 0) return null;
    const prevTo = new Date(from.getTime() - 1);
    const prevFrom = new Date(prevTo.getTime() - diffMs);
    return {
      dateFrom: prevFrom.toISOString().slice(0, 10),
      dateTo: prevTo.toISOString().slice(0, 10),
    };
  }, [dateFrom, dateTo]);

  const { data: previousSatisfactionData } = useQuery(
    [
      'reports-satisfaction-prev',
      accountId,
      satisfactionGroupBy,
      reportTypeFilter,
      previousPeriodRange?.dateFrom,
      previousPeriodRange?.dateTo,
    ],
    () =>
      fetchSatisfactionMetrics(accountId, {
        groupBy: satisfactionGroupBy,
        ...(reportTypeFilter ? { reportType: reportTypeFilter } : {}),
        dateFrom: previousPeriodRange?.dateFrom ?? '',
        dateTo: previousPeriodRange?.dateTo ?? '',
      }),
    {
      enabled: previousPeriodRange != null,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const handleUpdateStatus = async (
    reportId: number,
    newStatus: ReportStatus,
    note?: string
  ): Promise<void> => {
    setUpdatingReportId(reportId);
    try {
      await updateCallReport(reportId, {
        status: newStatus,
        ...(note ? { resolved_note: note } : {}),
      });
      queryCache.invalidateQueries(['reports-dashboard', accountId]);
      queryCache.invalidateQueries(['reports-satisfaction', accountId]);
      setExpandedReportId(null);
      setResolvedNoteText('');
    } catch {
      // silent
    } finally {
      setUpdatingReportId(null);
    }
  };

  const handleBulkResolve = async (): Promise<void> => {
    setBulkResolving(true);
    try {
      await bulkResolveReports(accountId);
      queryCache.invalidateQueries(['reports-dashboard', accountId]);
      queryCache.invalidateQueries(['reports-satisfaction', accountId]);
    } catch {
      // silent
    } finally {
      setBulkResolving(false);
    }
  };

  return (
    <div
      className={classNames(
        'px-4 py-4 md:px-6 md:py-6',
        isStandalone
          ? 'min-h-screen bg-gray-50'
          : 'flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray-50'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="md:text-2xl text-xl font-bold text-gray-900">Dashboard de Reportes</h2>
          <p className="mt-1 text-sm text-gray-500">
            Cuenta #{accountId} · Métricas de satisfacción y gestión de reportes
          </p>
        </div>
        {!isStandalone && (
          <button
            type="button"
            onClick={() => openStandaloneWindow(accountId)}
            className="hover:text-gray-600 text-gray-400"
            title="Abrir en nueva ventana"
          >
            <ExternalLink size={20} />
          </button>
        )}
      </div>

      {/* Satisfaction Summary */}
      <Section title="Satisfacción general" icon={OrbitIcons.BarChart3}>
        <div>
          {satisfactionLoading && (
            <p className="px-4 py-6 text-sm text-center text-gray-400">Calculando métricas…</p>
          )}
          {!satisfactionLoading && satisfactionData && (
            <>
              {previousSatisfactionData && (
                <div className="px-4 py-1.5 text-center text-[10px] font-medium text-gray-400 bg-gray-50 border-b border-gray-200">
                  Comparando con periodo anterior ({previousPeriodRange?.dateFrom} a{' '}
                  {previousPeriodRange?.dateTo})
                </div>
              )}
              <div className="sm:grid-cols-4 grid grid-cols-2 gap-px bg-gray-100">
                <div className="flex flex-col items-center px-4 py-4 bg-white">
                  <p className="text-2xl font-bold text-gray-900">
                    {satisfactionData.summary.total_reports}
                  </p>
                  <p className="mt-1 text-[11px] font-medium text-gray-500">Total</p>
                  {previousSatisfactionData && (
                    <DeltaBadge
                      current={satisfactionData.summary.total_reports}
                      previous={previousSatisfactionData.summary.total_reports}
                    />
                  )}
                </div>
                <div className="flex flex-col items-center px-4 py-4 bg-white">
                  <p className="text-2xl font-bold text-green-600">
                    {satisfactionData.summary.positive}
                  </p>
                  <p className="mt-1 text-[11px] font-medium text-gray-500">Positivos</p>
                  {previousSatisfactionData && (
                    <DeltaBadge
                      current={satisfactionData.summary.positive}
                      previous={previousSatisfactionData.summary.positive}
                    />
                  )}
                </div>
                <div className="flex flex-col items-center px-4 py-4 bg-white">
                  <p className="text-2xl font-bold text-red-500">
                    {satisfactionData.summary.negative}
                  </p>
                  <p className="mt-1 text-[11px] font-medium text-gray-500">Negativos</p>
                  {previousSatisfactionData && (
                    <DeltaBadge
                      current={satisfactionData.summary.negative}
                      previous={previousSatisfactionData.summary.negative}
                      invert
                    />
                  )}
                </div>
                <div className="flex flex-col items-center px-4 py-4 bg-white">
                  <p
                    className={classNames(
                      'text-2xl font-bold',
                      satisfactionData.summary.satisfaction_rate != null &&
                        satisfactionData.summary.satisfaction_rate >= 70
                        ? 'text-green-600'
                        : satisfactionData.summary.satisfaction_rate != null &&
                          satisfactionData.summary.satisfaction_rate >= 40
                        ? 'text-yellow-600'
                        : 'text-red-500'
                    )}
                  >
                    {satisfactionData.summary.satisfaction_rate != null
                      ? `${satisfactionData.summary.satisfaction_rate}%`
                      : '—'}
                  </p>
                  <p className="mt-1 text-[11px] font-medium text-gray-500">Satisfacción</p>
                  {previousSatisfactionData &&
                    satisfactionData.summary.satisfaction_rate != null &&
                    previousSatisfactionData.summary.satisfaction_rate != null && (
                      <DeltaBadge
                        current={satisfactionData.summary.satisfaction_rate}
                        previous={previousSatisfactionData.summary.satisfaction_rate}
                      />
                    )}
                </div>
              </div>

              {satisfactionData.by_type.length > 0 && (
                <div className="px-4 py-3 border-t border-gray-200">
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    Por tipo de reporte
                  </p>
                  <div className="space-y-2">
                    {satisfactionData.by_type.map((bt) => (
                      <div key={bt.report_type} className="flex items-center gap-3">
                        <span className="flex-1 min-w-0 text-xs font-medium text-gray-900 truncate">
                          {REPORT_TYPE_LABELS[bt.report_type] ?? bt.report_type}
                        </span>
                        <div className="flex items-center flex-shrink-0 gap-2">
                          <div className="flex items-center gap-1">
                            <Icon name={OrbitIcons.ThumbsUp} size={12} className="text-green-600" />
                            <span className="text-xs font-semibold text-green-600">
                              {bt.positive}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name={OrbitIcons.ThumbsDown} size={12} className="text-red-500" />
                            <span className="text-xs font-semibold text-red-500">
                              {bt.negative}
                            </span>
                          </div>
                          <span className="w-12 text-xs font-bold text-right text-gray-500">
                            {bt.satisfaction_rate}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {satisfactionData.timeline.length > 0 && (
                <div className="px-4 py-3 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
                      {TIMELINE_TITLE[satisfactionGroupBy]}
                    </p>
                    <div className="flex items-center gap-1">
                      {(Object.keys(GROUP_BY_LABELS) as SatisfactionGroupBy[]).map((g) => (
                        <button
                          key={g}
                          type="button"
                          className={classNames(
                            'rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors',
                            satisfactionGroupBy === g
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          )}
                          onClick={() => setSatisfactionGroupBy(g)}
                        >
                          {GROUP_BY_LABELS[g]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {satisfactionData.timeline.map((t) => {
                      const barWidth =
                        satisfactionData.summary.total_reports > 0
                          ? Math.round((t.total / satisfactionData.summary.total_reports) * 100)
                          : 0;
                      return (
                        <div key={t.period} className="flex items-center gap-2">
                          <span className="w-20 flex-shrink-0 text-[10px] font-medium text-gray-400 truncate">
                            {t.period}
                          </span>
                          <div className="relative flex-1 h-4 overflow-hidden bg-gray-100 rounded">
                            {t.total > 0 && (
                              <>
                                <div
                                  className="absolute inset-y-0 left-0 bg-green-400 rounded-l"
                                  style={{
                                    width: `${(t.positive / t.total) * barWidth}%`,
                                  }}
                                />
                                <div
                                  className="absolute inset-y-0 bg-red-400 rounded-r"
                                  style={{
                                    left: `${(t.positive / t.total) * barWidth}%`,
                                    width: `${(t.negative / t.total) * barWidth}%`,
                                  }}
                                />
                              </>
                            )}
                          </div>
                          <span className="w-10 flex-shrink-0 text-right text-[10px] font-bold text-gray-500">
                            {t.satisfaction_rate}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
          {!satisfactionLoading && !satisfactionData && (
            <p className="px-4 py-6 text-sm text-center text-gray-400">
              No hay datos de satisfacción disponibles.
            </p>
          )}
        </div>
      </Section>

      {/* Reports list */}
      <Section
        title="Reportes de la cuenta"
        icon={OrbitIcons.FileText}
        right={
          reportsPayload?.total_count != null ? (
            <span className="text-xs font-medium text-gray-500">
              {reportsPayload.total_count} en total
            </span>
          ) : null
        }
      >
        <div>
          {/* Filters */}
          <div className="border-b border-gray-200 px-4 py-2.5 space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mr-1">
                Estado:
              </span>
              <button
                type="button"
                className={classNames(
                  'rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors',
                  statusFilter === ''
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                )}
                onClick={() => {
                  setStatusFilter('');
                  setPage(1);
                }}
              >
                Todos
              </button>
              {ALL_STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={classNames(
                    'rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors',
                    statusFilter === s
                      ? `${REPORT_STATUS_COLORS[s].bg} ${REPORT_STATUS_COLORS[s].text}`
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  )}
                  onClick={() => {
                    setStatusFilter(s);
                    setPage(1);
                  }}
                >
                  {REPORT_STATUS_LABELS[s]}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mr-1">
                Calidad:
              </span>
              <button
                type="button"
                className={classNames(
                  'rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors',
                  valueFilter === ''
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                )}
                onClick={() => {
                  setValueFilter('');
                  setPage(1);
                }}
              >
                Todos
              </button>
              <button
                type="button"
                className={classNames(
                  'rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors',
                  valueFilter === 'positive'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                )}
                onClick={() => {
                  setValueFilter('positive');
                  setPage(1);
                }}
              >
                Positivo
              </button>
              <button
                type="button"
                className={classNames(
                  'rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors',
                  valueFilter === 'negative'
                    ? 'bg-red-100 text-red-600'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                )}
                onClick={() => {
                  setValueFilter('negative');
                  setPage(1);
                }}
              >
                Negativo
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mr-1">
                Tipo:
              </span>
              <button
                type="button"
                className={classNames(
                  'rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors',
                  reportTypeFilter === ''
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                )}
                onClick={() => {
                  setReportTypeFilter('');
                  setPage(1);
                }}
              >
                Todos
              </button>
              {ALL_REPORT_TYPES.map((rt) => (
                <button
                  key={rt}
                  type="button"
                  className={classNames(
                    'rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors',
                    reportTypeFilter === rt
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  )}
                  onClick={() => {
                    setReportTypeFilter(rt);
                    setPage(1);
                  }}
                >
                  {REPORT_TYPE_LABELS[rt]}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mr-1">
                Fecha:
              </span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                className="rounded border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              <span className="text-[10px] text-gray-400">a</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                className="rounded border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              {(dateFrom || dateTo) && (
                <button
                  type="button"
                  className="rounded-full px-2 py-1 text-[11px] font-semibold text-gray-500 hover:bg-gray-200 transition-colors"
                  onClick={() => {
                    setDateFrom('');
                    setDateTo('');
                    setPage(1);
                  }}
                >
                  Limpiar
                </button>
              )}
              <div className="flex-1" />
              <button
                type="button"
                disabled={bulkResolving}
                onClick={() => bulkResolveModalRef.current?.show()}
                className="rounded bg-green-600 px-3 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-green-700 disabled:opacity-50"
              >
                {bulkResolving ? 'Cerrando…' : 'Cerrar todos los abiertos'}
              </button>
            </div>
          </div>

          <Modal
            ref={bulkResolveModalRef}
            actionFunc={handleBulkResolve}
            actionText="Cerrar todos"
            cancelText="Cancelar"
            isDanger
            loading={bulkResolving}
            loadingText="Cerrando…"
          >
            <h3 className="text-lg font-bold text-gray-900">Cerrar reportes abiertos</h3>
            <p className="mt-3 text-sm text-gray-600">
              ¿Estás seguro de que quieres cerrar todos los reportes con estado{' '}
              <span className="font-semibold text-blue-700">Abierto</span> y{' '}
              <span className="font-semibold text-yellow-700">En progreso</span> de esta cuenta?
            </p>
            <p className="mt-2 text-xs text-gray-400">
              Esta acción cambiará su estado a "Resuelto". No se puede deshacer masivamente.
            </p>
          </Modal>

          {reportsLoading && (
            <p className="px-4 py-6 text-sm text-center text-gray-400">Cargando reportes…</p>
          )}
          {reportsError && (
            <p className="px-4 py-6 text-sm text-center text-gray-400">
              No se pudieron cargar los reportes. Revisa permisos de la API.
            </p>
          )}
          {!reportsLoading && !reportsError && (reportsPayload?.reports ?? []).length === 0 && (
            <p className="px-4 py-6 text-sm text-center text-gray-400">
              No hay reportes para esta cuenta.
            </p>
          )}
          {!reportsLoading &&
            !reportsError &&
            (reportsPayload?.reports ?? []).map((r: CallReport) => {
              const isExpanded = expandedReportId === r.id;
              const statusColor = REPORT_STATUS_COLORS[r.status] ?? REPORT_STATUS_COLORS.open;
              return (
                <div key={r.id} className="last:border-b-0 border-b border-gray-200">
                  <button
                    type="button"
                    className={classNames(
                      'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50',
                      isExpanded && 'bg-gray-50'
                    )}
                    onClick={() => {
                      setExpandedReportId(isExpanded ? null : r.id);
                      setResolvedNoteText(r.resolved_note ?? '');
                    }}
                  >
                    <div className="h-9 w-9 flex items-center justify-center flex-shrink-0 bg-gray-100 rounded">
                      <Icon
                        name={
                          r.report_value === 'positive'
                            ? OrbitIcons.ThumbsUp
                            : OrbitIcons.ThumbsDown
                        }
                        size={16}
                        className={
                          r.report_value === 'positive' ? 'text-green-600' : 'text-red-500'
                        }
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900">
                          {REPORT_TYPE_LABELS[r.report_type] ?? r.report_type}
                        </p>
                        <span
                          className={classNames(
                            'rounded px-1.5 py-0.5 text-[10px] font-bold uppercase',
                            r.report_value === 'positive'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-600'
                          )}
                        >
                          {r.report_value === 'positive' ? 'Positivo' : 'Negativo'}
                        </span>
                        <span
                          className={classNames(
                            'rounded px-1.5 py-0.5 text-[10px] font-bold uppercase',
                            statusColor.bg,
                            statusColor.text
                          )}
                        >
                          {REPORT_STATUS_LABELS[r.status] ?? r.status}
                        </span>
                      </div>
                      {r.note && <p className="mt-1 text-xs italic text-gray-500">"{r.note}"</p>}
                      {r.resolved_note && (
                        <p className="mt-0.5 text-xs text-green-700">
                          Resolución: {r.resolved_note}
                        </p>
                      )}
                      <p className="mt-0.5 text-[10px] text-gray-400">
                        {r.first_name || r.last_name
                          ? `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim()
                          : `Usuario #${r.user_id}`}
                        {r.phone ? ` · Ext. ${r.phone}` : ''} · {formatReportDate(r.created_at)}
                      </p>
                    </div>
                    <Icon
                      name={isExpanded ? OrbitIcons.ChevronRight : OrbitIcons.ChevronLeft}
                      size={16}
                      className={classNames(
                        'flex-shrink-0 mt-1 transition-transform text-gray-400',
                        isExpanded && 'rotate-90'
                      )}
                    />
                  </button>

                  {isExpanded && (
                    <div className="bg-gray-50 px-4 py-3 space-y-3">
                      {/* Recording player */}
                      {r.freeswitch_cdr_id && (
                        <div>
                          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">
                            Grabación
                          </p>
                          <div className="flex items-center gap-3">
                            <RecordingPlayer recordingUrl={r.recording_url ?? undefined} />
                            <span
                              className="text-[10px] font-mono text-gray-400 truncate"
                              title={r.freeswitch_cdr_id}
                            >
                              {r.freeswitch_cdr_id}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Diagnostic data */}
                      {r.diagnostic_data && (
                        <div>
                          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                            Diagnóstico al momento del reporte
                          </p>
                          <div className="sm:grid-cols-3 grid grid-cols-1 gap-2">
                            {r.diagnostic_data.network && (
                              <div className="px-3 py-2 bg-white border border-gray-200 rounded">
                                <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">
                                  Red
                                </p>
                                <div className="space-y-0.5 text-xs text-gray-700">
                                  {r.diagnostic_data.network.latencyMs != null && (
                                    <p>
                                      Latencia:{' '}
                                      <span className="font-semibold">
                                        {r.diagnostic_data.network.latencyMs}ms
                                      </span>
                                    </p>
                                  )}
                                  {r.diagnostic_data.network.effectiveType && (
                                    <p>
                                      Tipo:{' '}
                                      <span className="font-semibold">
                                        {r.diagnostic_data.network.effectiveType}
                                      </span>
                                    </p>
                                  )}
                                  {r.diagnostic_data.network.downlink != null && (
                                    <p>
                                      Bajada:{' '}
                                      <span className="font-semibold">
                                        {r.diagnostic_data.network.downlink} Mbps
                                      </span>
                                    </p>
                                  )}
                                  {r.diagnostic_data.network.rtt != null && (
                                    <p>
                                      RTT:{' '}
                                      <span className="font-semibold">
                                        {r.diagnostic_data.network.rtt}ms
                                      </span>
                                    </p>
                                  )}
                                  <p>
                                    Online:{' '}
                                    <span
                                      className={classNames(
                                        'font-semibold',
                                        r.diagnostic_data.network.online
                                          ? 'text-green-600'
                                          : 'text-red-500'
                                      )}
                                    >
                                      {r.diagnostic_data.network.online ? 'Sí' : 'No'}
                                    </span>
                                  </p>
                                </div>
                              </div>
                            )}
                            {r.diagnostic_data.audio && (
                              <div className="px-3 py-2 bg-white border border-gray-200 rounded">
                                <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">
                                  Audio
                                </p>
                                <div className="space-y-0.5 text-xs text-gray-700">
                                  <p>
                                    Micrófonos:{' '}
                                    <span className="font-semibold">
                                      {r.diagnostic_data.audio.inputCount ?? 0}
                                    </span>
                                  </p>
                                  <p>
                                    Salidas:{' '}
                                    <span className="font-semibold">
                                      {r.diagnostic_data.audio.outputCount ?? 0}
                                    </span>
                                  </p>
                                  {(r.diagnostic_data.audio.inputDevices ?? []).map((d, i) => (
                                    <p
                                      key={i}
                                      className="text-[10px] text-gray-500 truncate"
                                      title={d.label}
                                    >
                                      🎤 {d.label}
                                    </p>
                                  ))}
                                  {(r.diagnostic_data.audio.outputDevices ?? []).map((d, i) => (
                                    <p
                                      key={i}
                                      className="text-[10px] text-gray-500 truncate"
                                      title={d.label}
                                    >
                                      🔊 {d.label}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            )}
                            {r.diagnostic_data.system && (
                              <div className="px-3 py-2 bg-white border border-gray-200 rounded">
                                <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">
                                  Sistema
                                </p>
                                <div className="space-y-0.5 text-xs text-gray-700">
                                  {r.diagnostic_data.system.browser && (
                                    <p>
                                      Navegador:{' '}
                                      <span className="font-semibold">
                                        {r.diagnostic_data.system.browser}
                                      </span>
                                    </p>
                                  )}
                                  {r.diagnostic_data.system.os && (
                                    <p>
                                      SO:{' '}
                                      <span className="font-semibold">
                                        {r.diagnostic_data.system.os}
                                      </span>
                                    </p>
                                  )}
                                  {r.diagnostic_data.system.hardwareConcurrency != null && (
                                    <p>
                                      CPU cores:{' '}
                                      <span className="font-semibold">
                                        {r.diagnostic_data.system.hardwareConcurrency}
                                      </span>
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
                        Cambiar estado
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {ALL_STATUSES.map((s) => (
                          <button
                            key={s}
                            type="button"
                            disabled={r.status === s || updatingReportId === r.id}
                            className={classNames(
                              'rounded px-3 py-1.5 text-xs font-semibold transition-all',
                              r.status === s
                                ? `${REPORT_STATUS_COLORS[s].bg} ${REPORT_STATUS_COLORS[s].text} ring-2 ring-offset-1 ring-current`
                                : 'bg-gray-100 text-gray-500 hover:bg-white disabled:opacity-40'
                            )}
                            onClick={() => {
                              if (s === 'resolved') return;
                              handleUpdateStatus(r.id, s);
                            }}
                          >
                            {REPORT_STATUS_LABELS[s]}
                          </button>
                        ))}
                      </div>

                      <div className="space-y-2">
                        <label className="block">
                          <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
                            Nota de resolución
                          </span>
                          <textarea
                            className="placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 w-full px-3 py-2 mt-1 text-sm text-gray-900 bg-white border border-gray-200 rounded"
                            rows={2}
                            placeholder="Describe la resolución o acción tomada…"
                            value={resolvedNoteText}
                            onChange={(e) => setResolvedNoteText(e.target.value)}
                          />
                        </label>
                        <button
                          type="button"
                          disabled={updatingReportId === r.id}
                          className="flex items-center gap-1.5 rounded bg-green-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-green-700 disabled:opacity-50"
                          onClick={() =>
                            handleUpdateStatus(r.id, 'resolved', resolvedNoteText || undefined)
                          }
                        >
                          <Icon name={OrbitIcons.CheckCircle2} size={14} />
                          {updatingReportId === r.id ? 'Guardando…' : 'Marcar como resuelto'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

          {/* Pagination */}
          {!reportsLoading && !reportsError && reportsPayload && reportsPayload.total_count > 20 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="flex items-center gap-1 rounded px-2.5 py-1.5 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Icon name={OrbitIcons.ChevronLeft} size={14} />
                Anterior
              </button>
              <span className="text-xs font-medium text-gray-500">
                Pág. {page} de {Math.ceil(reportsPayload.total_count / 20)}
              </span>
              <button
                type="button"
                disabled={page >= Math.ceil(reportsPayload.total_count / 20)}
                onClick={() => setPage((p) => p + 1)}
                className="flex items-center gap-1 rounded px-2.5 py-1.5 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Siguiente
                <Icon name={OrbitIcons.ChevronRight} size={14} />
              </button>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
};
